import { IDeliverySelectorData } from "../../api/estore/checkout/interfaces/delivery-selector-data-interface";
import { IDeliverySelectorOutputOption } from "./interfaces/delivery-selector-output-option-interface";

export class DeliverySelectorOutputStateManager {
    readonly LOCAL_STORAGE_KEY = "delivery-selector-data";
    outputState: IDeliverySelectorOutputOption[] = [];
    // Trigger when selected option is changed
    onSelectedChanged: { (selectedOption: IDeliverySelectorOutputOption) : void }[] = [];
    // Trigger when any fields are changed
    onPropertiesChanged: { (selectedOption: IDeliverySelectorOutputOption) : void }[] = [];
    // Trigger for all changes
    onOptionChanged: { (selectedOption: IDeliverySelectorOutputOption) : void }[] = [];

    constructor(state: IDeliverySelectorData) {
        let storageState = this.getOutputStateFromLocalStorage();

        if (storageState && storageState.length > 0) {
            this.outputState = storageState;
            this.updateStateFromOutputState(state, storageState);
        } else {
            storageState = this.createOutputStateFromInitialState(state);
            this.outputState = storageState;
            this.updateStateFromOutputState(state, storageState, false);
            if (storageState) {
                this.saveOutputState();
            }
        }
    }

    public setOptionSelected(optionId: string, checked: boolean) {
        const option = this.getOrCreateOutputOption(optionId);
        this.outputState.forEach(os => os.selected = false);
        option.selected = true;
        option.providerCheck = checked;
        this.saveOutputState();
        this.onSelectedChanged.forEach(d => d(option));
        this.onOptionChanged.forEach(d => d(option));
    }

    public setOptionChecked(optionId: string, checked: boolean) {
        const option = this.getOrCreateOutputOption(optionId);
        option.providerCheck = checked;
        this.saveOutputState();
        this.onPropertiesChanged.forEach(d => d(option));
        this.onOptionChanged.forEach(d => d(option));
    }

    public setOptionInputValue(optionId: string, value: string) {
        const option = this.getOrCreateOutputOption(optionId);
        option.providerInputMessage = value;
        this.saveOutputState();
        this.onPropertiesChanged.forEach(d => d(option));
        this.onOptionChanged.forEach(d => d(option));
    }

    public setOptionPickupPointId(optionId: string, pickupPointId: string) {
        const option = this.getOrCreateOutputOption(optionId);
        option.providerPickupPointId = pickupPointId;
        this.saveOutputState();
        this.onPropertiesChanged.forEach(d => d(option));
        this.onOptionChanged.forEach(d => d(option));
    }

    private getOrCreateOutputOption(optionId: string): IDeliverySelectorOutputOption {
        let option = this.outputState.filter(o => o.id === optionId)[0];
        if (!option) {
            option = { id: optionId } as IDeliverySelectorOutputOption;
            this.outputState.push(option);
        }

        return option;
    }

    public updateStateFromOutputState(state: IDeliverySelectorData, outState: IDeliverySelectorOutputOption[] = null, preselectFirst: boolean = true) {
        outState ??= this.outputState;

        if (!outState || outState.length < 1) {
            return;
        }

        state.groups.forEach(g => {
            // Clear initial selected state
            g.selected = false;

            g.options.forEach(o => {
                // Clear initial selected state
                o.selected = false;

                const outStateOption = outState.filter(os => os.id === o.id)[0];
                if (!outStateOption) { 
                    if (o.providerCheckbox === true) {
                        o.providerCheckboxChecked = true;
                    }

                    return;
                }

                if (outStateOption.selected === true) {
                    g.selected = outStateOption.selected;
                }

                o.selected = outStateOption.selected;
                o.providerCheckboxChecked = outStateOption.providerCheck;
                o.providerInputValue = outStateOption.providerInputMessage;
                if (outStateOption.providerPickupPointId && o.providerPickupPoints) {
                    o.providerPickupPoints.forEach(pp => {
                        pp.selected = pp.id === outStateOption.providerPickupPointId;
                    });
                }
            });
        });

        if (preselectFirst) {
            this.ensureAtLeastOneSelectedInState(state);
        }
    }

    private createOutputStateFromInitialState(state: IDeliverySelectorData) : IDeliverySelectorOutputOption[] {
        if (!state) {
            return null;
        }

        const outState: IDeliverySelectorOutputOption[] = [];

        state.groups.forEach(g => {
            g.options.forEach(o => {
                outState.push({
                    id: o.id,
                    selected: o.selected,
                    providerPickupPointId: o.providerPickupPoints?.filter(pp => pp.selected)[0]?.id,
                    providerCheck: o.providerCheckbox,
                } as IDeliverySelectorOutputOption);
            });
        });

        return outState;
    }

    private ensureAtLeastOneSelectedInState(state: IDeliverySelectorData) {
        const allOptions = state.groups.map(g => g.options).flat();

        if (allOptions.filter(o => o.selected).length === 0) {
            if (state.groups.length > 0) {
                state.groups[0].selected = true;
                if (state.groups[0].options.length > 0) {
                    state.groups[0].options[0].selected = true;
                    const outStateItem = this.getOrCreateOutputOption(state.groups[0].options[0].id);
                    outStateItem.selected = true;
                }
            }
        }

        const hasPickupPoints = allOptions.filter(o => o.providerPickupPointSelector);
        hasPickupPoints.forEach(o => {
            if (o.providerPickupPoints.length > 0 && o.providerPickupPoints.filter(pp => pp.selected).length === 0) {
                o.providerPickupPoints[0].selected = true;
                const outStateItem = this.getOrCreateOutputOption(o.id);
                outStateItem.providerPickupPointId = o.providerPickupPoints[0].id;
            }
        });

        this.saveOutputState();
    }

    // Local storage handling
    private getOutputStateFromLocalStorage() : IDeliverySelectorOutputOption[]{
        const localStorageItem = window.localStorage.getItem(this.LOCAL_STORAGE_KEY);
        if (!localStorageItem) { return null; }
        return JSON.parse(localStorageItem);
    }

    private setOutputStateInLocalStorage(outState: IDeliverySelectorOutputOption[]) {
        if (!outState) {
            window.localStorage.removeItem(this.LOCAL_STORAGE_KEY);
            return;
        }

        const localStorageItem = JSON.stringify(outState);
        window.localStorage.setItem(this.LOCAL_STORAGE_KEY, localStorageItem);
    }

    private saveOutputState() {
        this.setOutputStateInLocalStorage(this.outputState);
    }
    // #Local storage handling
}